import { noop } from 'lodash';
import { useMemo } from 'react';

import { VisitMedicalInformation, VisitMedicalInformationQuery } from '@dreryk/egabinet-visit-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { dateFormatter, Ordering, Translate } from '@dreryk/react-common';
import {
   colorsMatrix,
   createDynamicColumnsWidth,
   StatusBadge,
   StyledFlex,
   TableColumnBackgroundColorResolver,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';

import { medicalInformationTranslationKeys } from '../../../../translations';

import { AvailableMedicalInformationTypes } from './AddMedicalInformationLink';
import { MedicalInformationDetailsModalLoader } from './MedicalInformationDetailsModal.loader';
import { MedicalInformationDetailsModalOpener } from './MedicalInformationDetailsModal.opener';

const DEFAULT_ORDERINGS: Ordering<VisitMedicalInformation>[] = [
   {
      descending: true,
      property: 'creationDate',
   },
];

export const useMedicalInformationDataConfiguration = (
   visitId: number
): LazyLoadedDataConfigurationByCqrsQuery<VisitMedicalInformation> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<VisitMedicalInformation>(() => {
      const query = new VisitMedicalInformationQuery();
      query.visitId = visitId;
      return query;
   }, DEFAULT_ORDERINGS);
};

export const useMedicalInformationTableColumns = (): TableColumnConfiguration<VisitMedicalInformation>[] => {
   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         type: '100%',
         creationDate: '104px',
         options: '36px',
      });

      const getBackgroundColor: TableColumnBackgroundColorResolver<VisitMedicalInformation> = (props) =>
         props.bean.removed ? colorsMatrix.red.lighter : undefined;

      return [
         {
            name: 'type',
            label: medicalInformationTranslationKeys.table.type,
            accessor: 'type',
            width: columnsWidth.type,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean }: ValueRendererProps<VisitMedicalInformation>) => (
               <Translate>
                  {medicalInformationTranslationKeys.categories[bean.type as AvailableMedicalInformationTypes]}
               </Translate>
            ),
         },
         {
            name: 'creationDate',
            label: medicalInformationTranslationKeys.table.creationDate,
            accessor: noop,
            width: columnsWidth.creationDate,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean }: ValueRendererProps<VisitMedicalInformation>) => (
               <StyledFlex gap={2} direction={'column'}>
                  <span>{dateFormatter(bean.creationDate)}</span>
                  {bean.removed ? (
                     <StatusBadge testId={'medicalInformationStatus'} type={'unapproved'} compact>
                        <Translate>{medicalInformationTranslationKeys.table.removed}</Translate>
                     </StatusBadge>
                  ) : null}
               </StyledFlex>
            ),
         },
         {
            name: 'options',
            accessor: noop,
            width: columnsWidth.options,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean }: ValueRendererProps<VisitMedicalInformation>) => (
               <MedicalInformationDetailsModalLoader medicalInformationId={bean.medicalInformationId}>
                  {({ medicalInformation }) => (
                     <MedicalInformationDetailsModalOpener medicalInformation={medicalInformation} />
                  )}
               </MedicalInformationDetailsModalLoader>
            ),
         },
      ];
   }, []);
};
